import { FC, ReactElement, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { useEffectOnce } from 'react-use';

import { CandidatePersonalDataForm } from '../../containers';
import { candidateNeedsToUpdatePersonalData } from '../../helpers/registration';
import { CandidatePersonalDataFormData } from '../../models/Candidate/Candidate';
import { fetchCandidate, updateCandidate } from '../../redux/candidate/candidateActions';
import { useTypedDispatch, useTypedSelector } from '../../redux/store';
import { RoutePaths } from '../../Routes';

interface ConnectedCandidatePersonalDataFormProps {
    isRegistration?: boolean;
    submitButtonLabel?: string;
    className?: string;
}

const ConnectedCandidatePersonalDataForm: FC<ConnectedCandidatePersonalDataFormProps> = ({
    isRegistration,
    submitButtonLabel,
    className = '',
}): ReactElement => {
    const dispatch = useTypedDispatch();
    const navigate = useNavigate();

    const user = useTypedSelector(state => state.userReducer.user);

    const candidateIsLoading = useTypedSelector(state => state.candidateReducer.isLoading);
    const candidateError = useTypedSelector(state => state.candidateReducer.error);
    const candidate = useTypedSelector(state => state.candidateReducer.candidate);

    useEffectOnce((): void => {
        if (user?.uuid) {
            dispatch(fetchCandidate(user.uuid));
        }
    });

    useEffect((): void => {
        if (!candidate && user?.uuid) {
            dispatch(fetchCandidate(user.uuid));
        }
    }, [candidate, user, dispatch]);

    useEffect((): void => {
        const isValidCandidate = !candidateError && !candidateNeedsToUpdatePersonalData(candidate);

        if (isRegistration && !candidateIsLoading && isValidCandidate) {
            navigate(RoutePaths.candidateDashboard(candidate?.uuid), { replace: true });
        }
    }, [candidateError, candidate, isRegistration, candidateIsLoading, navigate]);

    const handleSubmit = (formData: CandidatePersonalDataFormData): void => {
        if (!candidate) return;

        const updatedCandidate = {
            ...candidate,
            ...formData,
        };

        dispatch(updateCandidate(updatedCandidate));
    };

    const handleLogout = (): void => navigate(RoutePaths.logout());

    return (
        <CandidatePersonalDataForm
            candidateIsLoading={candidateIsLoading}
            candidate={candidate}
            error={candidateError}
            submitButtonLabel={submitButtonLabel}
            onSubmit={handleSubmit}
            onLogout={isRegistration ? handleLogout : undefined}
            className={className}
        />
    );
};

export default ConnectedCandidatePersonalDataForm;
